import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Divider, Dropdown, Tag } from "antd";
import { X, ChevronDown, Trash2 } from "lucide-react";

import {
  getCompanyListWithStartAndEnd,
  resetQuickSelectedRow,
  saveAdvancedSelectedFilters,
} from "../../actionCreator/companyListingActionCreater";
import QuickSelection from "../../common/QuickSelection";
import CompanyHeaderFilter from "./CompanyHeaderFilter";

const EXCLUDED_KEYS = ["page", "size"];

const splitValues = (value = "") =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const CompanyNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");

  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });

  const companySelectionList = useSelector(
    (state) => state.companyListingReducer?.companySelectionList,
  );
  const totalRecords = useSelector(
    (state) => state.companyListingReducer?.totalCount,
  );
  const industryList = useSelector(
    (state) => state.companyListingReducer.industryList,
  );
  const employeeCountList = useSelector(
    (state) => state.companyListingReducer.employeeCountList,
  );
  const companyTypeList = useSelector(
    (state) => state.companyListingReducer.companyTypeList,
  );
  const revenueRangeList = useSelector(
    (state) => state.companyListingReducer.revenueRangeList,
  );

  useEffect(() => {
    if (companySelectionList?.length === 0) {
      setSelectedRange({ start: "", end: "" });
    }
  }, [companySelectionList]);

  const onFilterChange = (key, val) => {
    if (val !== undefined) {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const onChangeSelectedRange = (e) => {
    setSelectedRange((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onClearQuickSelection = () => {
    setSelectedRange({ start: "", end: "" });
    dispatch(saveAdvancedSelectedFilters({ quickSelectionRange: null }));
    dispatch(resetQuickSelectedRow([]));
  };

  const onClickQuickSelection = (range) => {
    const pageValues = {
      start: range ? range.start : selectedRange.start,
      end: range ? range.end : selectedRange.end,
    };

    dispatch(
      saveAdvancedSelectedFilters({
        quickSelectionRange: pageValues,
      }),
    );

    const allParamsObj = Object.fromEntries(searchParams.entries());

    dispatch(
      getCompanyListWithStartAndEnd(allParamsObj, {
        start: pageValues.start - 1,
        end: pageValues.end - 1,
      }),
    );

    if (page !== pageValues.start) {
      searchParams.set("page", pageValues.start);
      navigate(`${location.pathname}?${searchParams.toString()}`);
    }
  };

  const allParams = useMemo(() => {
    return Object.fromEntries(
      [...searchParams.entries()].filter(
        ([key]) => !EXCLUDED_KEYS.includes(key),
      ),
    );
  }, [searchParams]);

  const hasOtherFilters = Object.keys(allParams).length > 0;

  const formatValue = (key, value) => {
    const ids = splitValues(value);

    switch (key) {
      case "Revenue Range":
        return revenueRangeList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Company Type":
        return companyTypeList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Employee Count":
        return employeeCountList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Industry":
        return industryList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      default:
        return value;
    }
  };

  return (
    <div className="tw-flex tw-pb-3 tw-ml-2 tw-items-center tw-justify-end tw-gap-3">
      {hasOtherFilters && (
        <div className="tw-flex tw-items-center">
          {Object.entries(allParams).length > 0 && (
            <>
              <Dropdown
                trigger={["click", "hover"]}
                placement="bottomLeft"
                overlay={
                  <div className="tw-w-[260px] tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-3">
                    <div className="tw-font-semibold tw-text-xs tw-mb-2 tw-text-center">
                      All Active Filters ({Object.entries(allParams).length})
                    </div>

                    {Object.entries(allParams).map(([key, value]) => (
                      <div
                        key={key}
                        className="tw-flex tw-justify-between tw-text-sub-text tw-items-center tw-py-2"
                      >
                        <span className="tw-text-xs">
                          {key}:{" "}
                          <b className="tw-text-text-primary">
                            {formatValue(key, value)}
                          </b>
                        </span>
                        <X
                          size={14}
                          className="tw-cursor-pointer tw-text-slate-500 hover:tw-text-red-500"
                          onClick={() => onFilterChange(key)}
                        />
                      </div>
                    ))}

                    <Divider className="tw-my-2" />

                    <div
                      className="tw-text-[#FF1115] tw-text-xs tw-font-semibold tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-2"
                      onClick={() =>
                        navigate(location.pathname, { replace: true })
                      }
                    >
                      <Trash2 size={14} /> Clear all filters
                    </div>
                  </div>
                }
              >
                <Tag
                  className="!tw-border-0 tw-cursor-pointer !tw-py-1.5 !tw-px-3.5 !tw-rounded-full !tw-bg-[#EFEAFF] !tw-text-xs !tw-font-bold !tw-text-[#5D44FF] tw-flex tw-items-center tw-gap-1.5 tw-shadow-sm hover:!tw-bg-[#e5dfff] tw-transition-colors"
                >
                  Active Filter(s): + {Object.entries(allParams).length}
                  <ChevronDown size={14} className="tw-text-[#5D44FF]" />
                </Tag>
              </Dropdown>

              <div
                className="tw-text-[#FF1115] tw-text-xs tw-font-semibold tw-whitespace-nowrap tw-ml-2 tw-cursor-pointer"
                onClick={() => navigate(location.pathname, { replace: true })}
              >
                Clear all
              </div>
            </>
          )}
        </div>
      )}

      <QuickSelection
        selectedRange={selectedRange}
        onChangeSelectedRange={onChangeSelectedRange}
        onClickQuickSelection={onClickQuickSelection}
        onClearQuickSelection={onClearQuickSelection}
        isClearVisible={!!companySelectionList?.length}
        totalRecord={totalRecords}
      />

      <CompanyHeaderFilter />
    </div>
  );
};

export default CompanyNavigation;
